export type Language = 'en' | 'de' | 'it' | 'fr' | 'es' | 'ja' | 'ko' | 'zh';

// Inquiry type options – keep in sync with DB defaults
export const INQUIRY_TYPES = [
  'general_question',
  'instrument_purchase',
  'restoration_repair',
  'custom_build',
  'supplier_business',
  'press_collaboration',
] as const;
export type InquiryType = typeof INQUIRY_TYPES[number];

export interface Translations {
  brandName: string;
  tagline: string;
  subline: string;
  visitInstagram: string;
  visitFacebook: string;
  followText: string;
  copyright: string;
  imageCaption: string;
  metaTitle: string;
  metaDescription: string;
  // Navigation
  navHome: string;
  navAbout: string;
  navInstruments: string;
  navRestorations: string;
  navGallery: string;
  navContact: string;
  // About page
  aboutTitle: string;
  aboutIntro: string;
  aboutPhilosophy: string;
  aboutPhilosophyText: string;
  aboutCraftsmanship: string;
  aboutCraftsmanshipText: string;
  // Instruments page
  instrumentsTitle: string;
  instrumentsIntro: string;
  violinTitle: string;
  violinText: string;
  violaTitle: string;
  violaText: string;
  celloTitle: string;
  celloText: string;
  bassTitle: string;
  bassText: string;
  // Restorations page
  restorationsTitle: string;
  restorationsIntro: string;
  restorationsText: string;
  // Gallery page
  galleryTitle: string;
  galleryIntro: string;
  galleryEmpty: string;
  // Contact page
  contactTitle: string;
  contactIntro: string;
  contactName: string;
  contactEmail: string;
  contactSubject: string;
  contactMessage: string;
  contactSend: string;
  contactSuccess: string;
  contactError: string;
  contactRateLimit: string;
  contactNameRequired: string;
  contactEmailRequired: string;
  contactEmailInvalid: string;
  contactMessageRequired: string;
  contactInquiryType: string;
  contactCountry: string;
  // Inquiry type labels
  inquiryTypeGeneral: string;
  inquiryTypePurchase: string;
  inquiryTypeRestoration: string;
  inquiryTypeCustomBuild: string;
  inquiryTypeSupplier: string;
  inquiryTypePress: string;
  // Filters
  filterAll: string;
  filterViolin: string;
  filterViola: string;
  filterCello: string;
  filterBass: string;
  view360: string;
  view360Hint: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    brandName: 'Szoradi Instruments',
    tagline: 'Fine Violins, Violas, Cellos & Double Basses — Handcrafted in Berlin, Germany',
    subline: 'Artisan string instruments made for musicians worldwide.',
    visitInstagram: 'Visit Instagram',
    visitFacebook: 'Visit Facebook',
    followText: 'Follow Szoradi Instruments on social media to discover craftsmanship, sound, and creation.',
    copyright: '© 2025 Szoradi Instruments. All rights reserved.',
    imageCaption: 'Cello after Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Fine Violins, Violas, Cellos & Double Basses – Handcrafted in Berlin, Germany',
    metaDescription: 'Szoradi Instruments builds fine handcrafted violins, violas, cellos, and double basses in Berlin, Germany. Authentic craftsmanship for musicians worldwide.',
    navHome: 'Home',
    navAbout: 'About',
    navInstruments: 'Instruments',
    navRestorations: 'Restorations',
    navGallery: 'Gallery',
    navContact: 'Contact',
    aboutTitle: 'About the Workshop',
    aboutIntro: 'Szoradi Instruments is a Berlin-based workshop dedicated to the art of building fine string instruments by hand.',
    aboutPhilosophy: 'Philosophy',
    aboutPhilosophyText: 'Every instrument is a unique creation, shaped by tradition and refined through decades of experience. We believe that a truly great instrument must sing with its own voice — resonant, expressive, and alive.',
    aboutCraftsmanship: 'Craftsmanship',
    aboutCraftsmanshipText: 'Using only the finest tonewoods and time-honored techniques, each instrument is meticulously crafted from start to finish. Every curve, every joint, every layer of varnish is applied with care and intention.',
    instrumentsTitle: 'Instruments',
    instrumentsIntro: 'Each instrument is built entirely by hand, following the great traditions of classical instrument making.',
    violinTitle: 'Violins',
    violinText: 'Built on the models of the great Italian masters, our violins offer a warm, projecting tone with exceptional playability.',
    violaTitle: 'Violas',
    violaText: 'Rich, dark, and sonorous — our violas are crafted for depth of sound and comfort for the performer.',
    celloTitle: 'Cellos',
    celloText: 'Powerful and singing, our cellos are designed to fill concert halls with warmth and clarity.',
    bassTitle: 'Double Basses',
    bassText: 'From orchestral to solo repertoire, our double basses deliver deep resonance and remarkable responsiveness.',
    restorationsTitle: 'Restorations',
    restorationsIntro: 'Preserving the legacy of fine instruments through expert restoration.',
    restorationsText: 'We offer professional restoration services for antique and modern string instruments. From structural repairs to varnish restoration, every instrument receives the care and expertise it deserves. Our goal is to preserve the original character and tonal qualities while ensuring the instrument can continue to be played for generations.',
    galleryTitle: 'Gallery',
    galleryIntro: 'A selection of instruments from the workshop.',
    galleryEmpty: 'New works are being prepared. Please visit again soon.',
    contactTitle: 'Contact',
    contactIntro: 'Interested in commissioning an instrument or have a question? We would love to hear from you.',
    contactName: 'Name',
    contactEmail: 'Email',
    contactSubject: 'Subject',
    contactMessage: 'Message',
    contactSend: 'Send Message',
    contactSuccess: 'Your message has been sent successfully. We will respond as soon as possible.',
    contactError: 'An error occurred. Please try again.',
    contactRateLimit: 'Too many requests. Please try again later.',
    contactNameRequired: 'Please enter your name.',
    contactEmailRequired: 'Please enter your email address.',
    contactEmailInvalid: 'Please enter a valid email address.',
    contactMessageRequired: 'Please enter your message.',
    contactInquiryType: 'Inquiry Type',
    contactCountry: 'Country (optional)',
    inquiryTypeGeneral: 'General Question',
    inquiryTypePurchase: 'Instrument Purchase',
    inquiryTypeRestoration: 'Restoration & Repair',
    inquiryTypeCustomBuild: 'Custom Build',
    inquiryTypeSupplier: 'Supplier & Business',
    inquiryTypePress: 'Press & Collaboration',
    filterAll: 'All',
    filterViolin: 'Violins',
    filterViola: 'Violas',
    filterCello: 'Cellos',
    filterBass: 'Double Basses',
    view360: '360° View',
    view360Hint: 'Interactive 360° view available',
  },
  de: {
    brandName: 'Szoradi Instruments',
    tagline: 'Feine Violinen, Bratschen, Celli & Kontrabässe — Handgefertigt in Berlin, Deutschland',
    subline: 'Meisterhafte Streichinstrumente für Musiker weltweit.',
    visitInstagram: 'Instagram besuchen',
    visitFacebook: 'Facebook besuchen',
    followText: 'Folgen Sie Szoradi Instruments in den sozialen Medien und entdecken Sie Handwerkskunst, Klang und Kreation.',
    copyright: '© 2025 Szoradi Instruments. Alle Rechte vorbehalten.',
    imageCaption: 'Cello nach Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Feine Violinen, Bratschen, Celli & Kontrabässe – Handgefertigt in Berlin',
    metaDescription: 'Szoradi Instruments fertigt feine handgefertigte Violinen, Bratschen, Celli und Kontrabässe in Berlin, Deutschland. Authentische Handwerkskunst für Musiker weltweit.',
    navHome: 'Startseite',
    navAbout: 'Werkstatt',
    navInstruments: 'Instrumente',
    navRestorations: 'Restaurierungen',
    navGallery: 'Galerie',
    navContact: 'Kontakt',
    aboutTitle: 'Über die Werkstatt',
    aboutIntro: 'Szoradi Instruments ist eine Berliner Werkstatt, die sich der Kunst des handgefertigten Streichinstrumentenbaus widmet.',
    aboutPhilosophy: 'Philosophie',
    aboutPhilosophyText: 'Jedes Instrument ist eine einzigartige Schöpfung, geformt durch Tradition und verfeinert durch jahrzehntelange Erfahrung. Wir glauben, dass ein wahrhaft großes Instrument mit seiner eigenen Stimme singen muss — resonant, ausdrucksvoll und lebendig.',
    aboutCraftsmanship: 'Handwerkskunst',
    aboutCraftsmanshipText: 'Unter Verwendung nur der feinsten Tonhölzer und bewährter Techniken wird jedes Instrument von Anfang bis Ende sorgfältig gefertigt. Jede Kurve, jede Fuge, jede Lackschicht wird mit Sorgfalt und Intention aufgetragen.',
    instrumentsTitle: 'Instrumente',
    instrumentsIntro: 'Jedes Instrument wird vollständig von Hand gebaut und folgt den großen Traditionen des klassischen Instrumentenbaus.',
    violinTitle: 'Violinen',
    violinText: 'Nach den Modellen der großen italienischen Meister gebaut, bieten unsere Violinen einen warmen, tragfähigen Ton mit außergewöhnlicher Spielbarkeit.',
    violaTitle: 'Bratschen',
    violaText: 'Reich, dunkel und klangvoll — unsere Bratschen sind für Klangtiefe und Komfort des Spielers gefertigt.',
    celloTitle: 'Celli',
    celloText: 'Kraftvoll und singend, unsere Celli sind darauf ausgelegt, Konzertsäle mit Wärme und Klarheit zu füllen.',
    bassTitle: 'Kontrabässe',
    bassText: 'Vom Orchester- bis zum Solorepertoire liefern unsere Kontrabässe tiefe Resonanz und bemerkenswerte Ansprache.',
    restorationsTitle: 'Restaurierungen',
    restorationsIntro: 'Bewahrung des Erbes feiner Instrumente durch fachgerechte Restaurierung.',
    restorationsText: 'Wir bieten professionelle Restaurierungsdienstleistungen für antike und moderne Streichinstrumente. Von strukturellen Reparaturen bis zur Lackrestauration erhält jedes Instrument die Pflege und Expertise, die es verdient. Unser Ziel ist es, den ursprünglichen Charakter und die klanglichen Qualitäten zu bewahren und gleichzeitig sicherzustellen, dass das Instrument über Generationen hinweg gespielt werden kann.',
    galleryTitle: 'Galerie',
    galleryIntro: 'Eine Auswahl von Instrumenten aus der Werkstatt.',
    galleryEmpty: 'Neue Arbeiten werden vorbereitet. Bitte besuchen Sie uns bald wieder.',
    contactTitle: 'Kontakt',
    contactIntro: 'Interesse an einem Instrument oder eine Frage? Wir freuen uns auf Ihre Nachricht.',
    contactName: 'Name',
    contactEmail: 'E-Mail',
    contactSubject: 'Betreff',
    contactMessage: 'Nachricht',
    contactSend: 'Nachricht senden',
    contactSuccess: 'Ihre Nachricht wurde erfolgreich gesendet. Wir antworten so bald wie möglich.',
    contactError: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    contactRateLimit: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
    contactNameRequired: 'Bitte geben Sie Ihren Namen ein.',
    contactEmailRequired: 'Bitte geben Sie Ihre E-Mail-Adresse ein.',
    contactEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    contactMessageRequired: 'Bitte geben Sie Ihre Nachricht ein.',
    contactInquiryType: 'Art der Anfrage',
    contactCountry: 'Land (optional)',
    inquiryTypeGeneral: 'Allgemeine Frage',
    inquiryTypePurchase: 'Instrumentenkauf',
    inquiryTypeRestoration: 'Restaurierung & Reparatur',
    inquiryTypeCustomBuild: 'Sonderbau',
    inquiryTypeSupplier: 'Lieferant & Geschäftliches',
    inquiryTypePress: 'Presse & Kooperation',
    filterAll: 'Alle',
    filterViolin: 'Violinen',
    filterViola: 'Bratschen',
    filterCello: 'Celli',
    filterBass: 'Kontrabässe',
    view360: '360°-Ansicht',
    view360Hint: 'Interaktive 360°-Ansicht verfügbar',
  },
  it: {
    brandName: 'Szoradi Instruments',
    tagline: 'Violini, Viole, Violoncelli & Contrabbassi Pregiati — Fatti a Mano a Berlino, Germania',
    subline: 'Strumenti ad arco artigianali per musicisti di tutto il mondo.',
    visitInstagram: 'Visita Instagram',
    visitFacebook: 'Visita Facebook',
    followText: 'Segui Szoradi Instruments sui social media per scoprire artigianato, suono e creazione.',
    copyright: '© 2025 Szoradi Instruments. Tutti i diritti riservati.',
    imageCaption: 'Violoncello da Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Violini, Viole, Violoncelli & Contrabbassi Pregiati – Fatti a Mano a Berlino',
    metaDescription: 'Szoradi Instruments costruisce violini, viole, violoncelli e contrabbassi pregiati fatti a mano a Berlino, Germania. Artigianato autentico per musicisti di tutto il mondo.',
    navHome: 'Home',
    navAbout: 'Laboratorio',
    navInstruments: 'Strumenti',
    navRestorations: 'Restauri',
    navGallery: 'Galleria',
    navContact: 'Contatto',
    aboutTitle: 'Il Laboratorio',
    aboutIntro: 'Szoradi Instruments è un laboratorio berlinese dedicato all\'arte della costruzione di strumenti ad arco fatti a mano.',
    aboutPhilosophy: 'Filosofia',
    aboutPhilosophyText: 'Ogni strumento è una creazione unica, plasmata dalla tradizione e raffinata attraverso decenni di esperienza. Crediamo che un grande strumento debba cantare con la propria voce — risonante, espressivo e vivo.',
    aboutCraftsmanship: 'Artigianato',
    aboutCraftsmanshipText: 'Utilizzando solo i migliori legni tonali e tecniche collaudate, ogni strumento è meticolosamente realizzato dall\'inizio alla fine. Ogni curva, ogni giuntura, ogni strato di vernice è applicato con cura e intenzione.',
    instrumentsTitle: 'Strumenti',
    instrumentsIntro: 'Ogni strumento è costruito interamente a mano, seguendo le grandi tradizioni della liuteria classica.',
    violinTitle: 'Violini',
    violinText: 'Costruiti sui modelli dei grandi maestri italiani, i nostri violini offrono un tono caldo e proiettante con un\'eccezionale suonabilità.',
    violaTitle: 'Viole',
    violaText: 'Ricche, scure e sonore — le nostre viole sono create per la profondità del suono e il comfort dell\'esecutore.',
    celloTitle: 'Violoncelli',
    celloText: 'Potenti e cantanti, i nostri violoncelli sono progettati per riempire le sale da concerto di calore e chiarezza.',
    bassTitle: 'Contrabbassi',
    bassText: 'Dal repertorio orchestrale a quello solistico, i nostri contrabbassi offrono una risonanza profonda e una notevole reattività.',
    restorationsTitle: 'Restauri',
    restorationsIntro: 'Preservare l\'eredità di strumenti pregiati attraverso il restauro esperto.',
    restorationsText: 'Offriamo servizi di restauro professionale per strumenti ad arco antichi e moderni. Dalle riparazioni strutturali al restauro della vernice, ogni strumento riceve la cura e la competenza che merita.',
    galleryTitle: 'Galleria',
    galleryIntro: 'Una selezione di strumenti dal laboratorio.',
    galleryEmpty: 'Nuovi lavori sono in preparazione. Vi preghiamo di tornare presto.',
    contactTitle: 'Contatto',
    contactIntro: 'Interessati a commissionare uno strumento o avete una domanda? Saremo lieti di sentirvi.',
    contactName: 'Nome',
    contactEmail: 'Email',
    contactSubject: 'Oggetto',
    contactMessage: 'Messaggio',
    contactSend: 'Invia Messaggio',
    contactSuccess: 'Il messaggio è stato inviato con successo. Risponderemo il prima possibile.',
    contactError: 'Si è verificato un errore. Riprova.',
    contactRateLimit: 'Troppe richieste. Riprova più tardi.',
    contactNameRequired: 'Inserisci il tuo nome.',
    contactEmailRequired: 'Inserisci il tuo indirizzo email.',
    contactEmailInvalid: 'Inserisci un indirizzo email valido.',
    contactMessageRequired: 'Inserisci il tuo messaggio.',
    contactInquiryType: 'Tipo di richiesta',
    contactCountry: 'Paese (opzionale)',
    inquiryTypeGeneral: 'Domanda generale',
    inquiryTypePurchase: 'Acquisto strumento',
    inquiryTypeRestoration: 'Restauro e riparazione',
    inquiryTypeCustomBuild: 'Costruzione personalizzata',
    inquiryTypeSupplier: 'Fornitore e affari',
    inquiryTypePress: 'Stampa e collaborazione',
    filterAll: 'Tutti',
    filterViolin: 'Violini',
    filterViola: 'Viole',
    filterCello: 'Violoncelli',
    filterBass: 'Contrabbassi',
    view360: 'Vista a 360°',
    view360Hint: 'Vista interattiva a 360° disponibile',
  },
  fr: {
    brandName: 'Szoradi Instruments',
    tagline: 'Violons, Altos, Violoncelles & Contrebasses d\'Exception — Faits Main à Berlin, Allemagne',
    subline: 'Instruments à cordes artisanaux pour musiciens du monde entier.',
    visitInstagram: 'Visiter Instagram',
    visitFacebook: 'Visiter Facebook',
    followText: 'Suivez Szoradi Instruments sur les réseaux sociaux pour découvrir l\'artisanat, le son et la création.',
    copyright: '© 2025 Szoradi Instruments. Tous droits réservés.',
    imageCaption: 'Violoncelle d\'après Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Violons, Altos, Violoncelles & Contrebasses d\'Exception – Faits Main à Berlin',
    metaDescription: 'Szoradi Instruments fabrique des violons, altos, violoncelles et contrebasses d\'exception faits main à Berlin, Allemagne. Artisanat authentique pour musiciens du monde entier.',
    navHome: 'Accueil',
    navAbout: 'Atelier',
    navInstruments: 'Instruments',
    navRestorations: 'Restaurations',
    navGallery: 'Galerie',
    navContact: 'Contact',
    aboutTitle: 'L\'Atelier',
    aboutIntro: 'Szoradi Instruments est un atelier berlinois dédié à l\'art de la lutherie artisanale.',
    aboutPhilosophy: 'Philosophie',
    aboutPhilosophyText: 'Chaque instrument est une création unique, façonnée par la tradition et affinée par des décennies d\'expérience. Nous croyons qu\'un grand instrument doit chanter de sa propre voix — résonant, expressif et vivant.',
    aboutCraftsmanship: 'Artisanat',
    aboutCraftsmanshipText: 'En utilisant uniquement les meilleurs bois de lutherie et des techniques éprouvées, chaque instrument est méticuleusement fabriqué du début à la fin.',
    instrumentsTitle: 'Instruments',
    instrumentsIntro: 'Chaque instrument est entièrement fait main, suivant les grandes traditions de la lutherie classique.',
    violinTitle: 'Violons',
    violinText: 'Construits sur les modèles des grands maîtres italiens, nos violons offrent un son chaud et projeté avec une jouabilité exceptionnelle.',
    violaTitle: 'Altos',
    violaText: 'Riches, sombres et sonores — nos altos sont conçus pour la profondeur du son et le confort du musicien.',
    celloTitle: 'Violoncelles',
    celloText: 'Puissants et chantants, nos violoncelles sont conçus pour remplir les salles de concert de chaleur et de clarté.',
    bassTitle: 'Contrebasses',
    bassText: 'Du répertoire orchestral au solo, nos contrebasses offrent une résonance profonde et une réactivité remarquable.',
    restorationsTitle: 'Restaurations',
    restorationsIntro: 'Préserver l\'héritage des instruments fins par une restauration experte.',
    restorationsText: 'Nous proposons des services de restauration professionnelle pour les instruments à cordes anciens et modernes.',
    galleryTitle: 'Galerie',
    galleryIntro: 'Une sélection d\'instruments de l\'atelier.',
    galleryEmpty: 'De nouvelles œuvres sont en préparation. Revenez bientôt.',
    contactTitle: 'Contact',
    contactIntro: 'Intéressé par la commande d\'un instrument ou une question ? Nous serions ravis de vous entendre.',
    contactName: 'Nom',
    contactEmail: 'Email',
    contactSubject: 'Sujet',
    contactMessage: 'Message',
    contactSend: 'Envoyer',
    contactSuccess: 'Votre message a été envoyé avec succès. Nous vous répondrons dès que possible.',
    contactError: 'Une erreur s\'est produite. Veuillez réessayer.',
    contactRateLimit: 'Trop de demandes. Veuillez réessayer plus tard.',
    contactNameRequired: 'Veuillez entrer votre nom.',
    contactEmailRequired: 'Veuillez entrer votre adresse email.',
    contactEmailInvalid: 'Veuillez entrer une adresse email valide.',
    contactMessageRequired: 'Veuillez entrer votre message.',
    contactInquiryType: 'Type de demande',
    contactCountry: 'Pays (optionnel)',
    inquiryTypeGeneral: 'Question générale',
    inquiryTypePurchase: 'Achat d\'instrument',
    inquiryTypeRestoration: 'Restauration et réparation',
    inquiryTypeCustomBuild: 'Construction sur mesure',
    inquiryTypeSupplier: 'Fournisseur et affaires',
    inquiryTypePress: 'Presse et collaboration',
    filterAll: 'Tous',
    filterViolin: 'Violons',
    filterViola: 'Altos',
    filterCello: 'Violoncelles',
    filterBass: 'Contrebasses',
    view360: 'Vue à 360°',
    view360Hint: 'Vue interactive à 360° disponible',
  },
  es: {
    brandName: 'Szoradi Instruments',
    tagline: 'Violines, Violas, Violonchelos & Contrabajos Finos — Hechos a Mano en Berlín, Alemania',
    subline: 'Instrumentos de cuerda artesanales para músicos de todo el mundo.',
    visitInstagram: 'Visitar Instagram',
    visitFacebook: 'Visitar Facebook',
    followText: 'Sigue a Szoradi Instruments en redes sociales para descubrir artesanía, sonido y creación.',
    copyright: '© 2025 Szoradi Instruments. Todos los derechos reservados.',
    imageCaption: 'Violonchelo según Carlo Tononi',
    metaTitle: 'Szoradi Instruments | Violines, Violas, Violonchelos & Contrabajos Finos – Hechos a Mano en Berlín',
    metaDescription: 'Szoradi Instruments construye violines, violas, violonchelos y contrabajos finos hechos a mano en Berlín, Alemania.',
    navHome: 'Inicio',
    navAbout: 'Taller',
    navInstruments: 'Instrumentos',
    navRestorations: 'Restauraciones',
    navGallery: 'Galería',
    navContact: 'Contacto',
    aboutTitle: 'El Taller',
    aboutIntro: 'Szoradi Instruments es un taller berlinés dedicado al arte de la luthería artesanal.',
    aboutPhilosophy: 'Filosofía',
    aboutPhilosophyText: 'Cada instrumento es una creación única, moldeada por la tradición y refinada a través de décadas de experiencia.',
    aboutCraftsmanship: 'Artesanía',
    aboutCraftsmanshipText: 'Utilizando solo las mejores maderas tonales y técnicas consagradas, cada instrumento es meticulosamente fabricado de principio a fin.',
    instrumentsTitle: 'Instrumentos',
    instrumentsIntro: 'Cada instrumento está construido enteramente a mano, siguiendo las grandes tradiciones de la luthería clásica.',
    violinTitle: 'Violines',
    violinText: 'Construidos sobre los modelos de los grandes maestros italianos, nuestros violines ofrecen un tono cálido y proyectante.',
    violaTitle: 'Violas',
    violaText: 'Ricas, oscuras y sonoras — nuestras violas están diseñadas para la profundidad del sonido.',
    celloTitle: 'Violonchelos',
    celloText: 'Potentes y cantantes, nuestros violonchelos están diseñados para llenar salas de concierto con calidez y claridad.',
    bassTitle: 'Contrabajos',
    bassText: 'Del repertorio orquestal al solista, nuestros contrabajos ofrecen una resonancia profunda.',
    restorationsTitle: 'Restauraciones',
    restorationsIntro: 'Preservando el legado de instrumentos finos a través de la restauración experta.',
    restorationsText: 'Ofrecemos servicios profesionales de restauración para instrumentos de cuerda antiguos y modernos.',
    galleryTitle: 'Galería',
    galleryIntro: 'Una selección de instrumentos del taller.',
    galleryEmpty: 'Nuevos trabajos están en preparación. Vuelva pronto.',
    contactTitle: 'Contacto',
    contactIntro: '¿Interesado en encargar un instrumento o tiene una pregunta? Nos encantaría saber de usted.',
    contactName: 'Nombre',
    contactEmail: 'Correo electrónico',
    contactSubject: 'Asunto',
    contactMessage: 'Mensaje',
    contactSend: 'Enviar Mensaje',
    contactSuccess: 'Su mensaje ha sido enviado con éxito. Responderemos lo antes posible.',
    contactError: 'Se produjo un error. Inténtelo de nuevo.',
    contactRateLimit: 'Demasiadas solicitudes. Inténtelo más tarde.',
    contactNameRequired: 'Por favor, introduzca su nombre.',
    contactEmailRequired: 'Por favor, introduzca su correo electrónico.',
    contactEmailInvalid: 'Por favor, introduzca un correo electrónico válido.',
    contactMessageRequired: 'Por favor, introduzca su mensaje.',
    contactInquiryType: 'Tipo de consulta',
    contactCountry: 'País (opcional)',
    inquiryTypeGeneral: 'Pregunta general',
    inquiryTypePurchase: 'Compra de instrumento',
    inquiryTypeRestoration: 'Restauración y reparación',
    inquiryTypeCustomBuild: 'Construcción personalizada',
    inquiryTypeSupplier: 'Proveedor y negocios',
    inquiryTypePress: 'Prensa y colaboración',
    filterAll: 'Todos',
    filterViolin: 'Violines',
    filterViola: 'Violas',
    filterCello: 'Violonchelos',
    filterBass: 'Contrabajos',
    view360: 'Vista de 360°',
    view360Hint: 'Vista interactiva de 360° disponible',
  },
  ja: {
    brandName: 'Szoradi Instruments',
    tagline: '高級ヴァイオリン、ヴィオラ、チェロ、コントラバス — ドイツ・ベルリンで手作り',
    subline: '世界中の音楽家のための職人による弦楽器。',
    visitInstagram: 'Instagramを見る',
    visitFacebook: 'Facebookを見る',
    followText: 'Szoradi Instrumentsのソーシャルメディアをフォローして、職人技、音、創造をご覧ください。',
    copyright: '© 2025 Szoradi Instruments. 無断転載禁止。',
    imageCaption: 'カルロ・トノーニ様式のチェロ',
    metaTitle: 'Szoradi Instruments | 高級ヴァイオリン、ヴィオラ、チェロ、コントラバス – ドイツ・ベルリンで手作り',
    metaDescription: 'Szoradi Instrumentsは、ドイツ・ベルリンで高級ヴァイオリン、ヴィオラ、チェロ、コントラバスを手作りしています。',
    navHome: 'ホーム',
    navAbout: '工房',
    navInstruments: '楽器',
    navRestorations: '修復',
    navGallery: 'ギャラリー',
    navContact: 'お問い合わせ',
    aboutTitle: '工房について',
    aboutIntro: 'Szoradi Instrumentsは、手作りの弦楽器製作の芸術に専念するベルリンの工房です。',
    aboutPhilosophy: '哲学',
    aboutPhilosophyText: 'すべての楽器は、伝統によって形作られ、数十年の経験によって洗練されたユニークな作品です。',
    aboutCraftsmanship: '職人技',
    aboutCraftsmanshipText: '最高級のトーンウッドと伝統的な技術のみを使用し、各楽器は最初から最後まで丹念に製作されます。',
    instrumentsTitle: '楽器',
    instrumentsIntro: '各楽器は古典的な製作の伝統に従い、完全に手作りで製作されています。',
    violinTitle: 'ヴァイオリン',
    violinText: 'イタリアの偉大な巨匠のモデルに基づいて製作された私たちのヴァイオリンは、温かく響く音色を提供します。',
    violaTitle: 'ヴィオラ',
    violaText: '豊かで深く響く — 私たちのヴィオラは音の深みと演奏者の快適さのために製作されています。',
    celloTitle: 'チェロ',
    celloText: '力強く歌うように、私たちのチェロは温かさと明瞭さでコンサートホールを満たすように設計されています。',
    bassTitle: 'コントラバス',
    bassText: 'オーケストラからソロまで、私たちのコントラバスは深い共鳴と優れた応答性を提供します。',
    restorationsTitle: '修復',
    restorationsIntro: '専門的な修復による名器の遺産の保存。',
    restorationsText: '私たちはアンティークおよび現代の弦楽器のための専門的な修復サービスを提供しています。',
    galleryTitle: 'ギャラリー',
    galleryIntro: '工房からの楽器のセレクション。',
    galleryEmpty: '新しい作品を準備中です。またお越しください。',
    contactTitle: 'お問い合わせ',
    contactIntro: '楽器の注文やご質問がございましたら、お気軽にお問い合わせください。',
    contactName: 'お名前',
    contactEmail: 'メールアドレス',
    contactSubject: '件名',
    contactMessage: 'メッセージ',
    contactSend: '送信',
    contactSuccess: 'メッセージが正常に送信されました。できるだけ早くご返信いたします。',
    contactError: 'エラーが発生しました。もう一度お試しください。',
    contactRateLimit: 'リクエストが多すぎます。後でもう一度お試しください。',
    contactNameRequired: 'お名前を入力してください。',
    contactEmailRequired: 'メールアドレスを入力してください。',
    contactEmailInvalid: '有効なメールアドレスを入力してください。',
    contactMessageRequired: 'メッセージを入力してください。',
    contactInquiryType: 'お問い合わせの種類',
    contactCountry: '国（任意）',
    inquiryTypeGeneral: '一般的な質問',
    inquiryTypePurchase: '楽器の購入',
    inquiryTypeRestoration: '修復と修理',
    inquiryTypeCustomBuild: 'カスタムビルド',
    inquiryTypeSupplier: 'サプライヤー＆ビジネス',
    inquiryTypePress: 'プレス＆コラボレーション',
    filterAll: 'すべて',
    filterViolin: 'ヴァイオリン',
    filterViola: 'ヴィオラ',
    filterCello: 'チェロ',
    filterBass: 'コントラバス',
    view360: '360°ビュー',
    view360Hint: 'インタラクティブな360°ビューが利用可能',
  },
  ko: {
    brandName: 'Szoradi Instruments',
    tagline: '고급 바이올린, 비올라, 첼로 & 더블베이스 — 독일 베를린에서 수제작',
    subline: '전 세계 음악가를 위한 장인의 현악기.',
    visitInstagram: 'Instagram 방문',
    visitFacebook: 'Facebook 방문',
    followText: 'Szoradi Instruments의 소셜 미디어를 팔로우하여 장인정신, 소리, 창작을 발견하세요.',
    copyright: '© 2025 Szoradi Instruments. 모든 권리 보유.',
    imageCaption: '카를로 토노니 양식의 첼로',
    metaTitle: 'Szoradi Instruments | 고급 바이올린, 비올라, 첼로 & 더블베이스 – 독일 베를린에서 수제작',
    metaDescription: 'Szoradi Instruments는 독일 베를린에서 고급 바이올린, 비올라, 첼로, 더블베이스를 수제작합니다.',
    navHome: '홈',
    navAbout: '공방',
    navInstruments: '악기',
    navRestorations: '복원',
    navGallery: '갤러리',
    navContact: '문의',
    aboutTitle: '공방 소개',
    aboutIntro: 'Szoradi Instruments는 수제 현악기 제작의 예술에 전념하는 베를린의 공방입니다.',
    aboutPhilosophy: '철학',
    aboutPhilosophyText: '모든 악기는 전통에 의해 형성되고 수십 년의 경험으로 다듬어진 독창적인 작품입니다.',
    aboutCraftsmanship: '장인정신',
    aboutCraftsmanshipText: '최고급 음향목과 전통 기술만을 사용하여 각 악기를 처음부터 끝까지 꼼꼼하게 제작합니다.',
    instrumentsTitle: '악기',
    instrumentsIntro: '각 악기는 고전적인 악기 제작의 위대한 전통을 따라 완전히 수작업으로 제작됩니다.',
    violinTitle: '바이올린',
    violinText: '이탈리아 거장들의 모델을 기반으로 제작된 우리의 바이올린은 따뜻하고 울림있는 음색을 제공합니다.',
    violaTitle: '비올라',
    violaText: '풍부하고 깊고 울림있는 — 우리의 비올라는 음향의 깊이와 연주자의 편안함을 위해 제작됩니다.',
    celloTitle: '첼로',
    celloText: '강력하고 노래하는 듯한 우리의 첼로는 따뜻함과 명료함으로 콘서트홀을 채우도록 설계되었습니다.',
    bassTitle: '더블베이스',
    bassText: '오케스트라에서 솔로까지, 우리의 더블베이스는 깊은 울림과 뛰어난 반응성을 제공합니다.',
    restorationsTitle: '복원',
    restorationsIntro: '전문적인 복원을 통해 명기의 유산을 보존합니다.',
    restorationsText: '골동품 및 현대 현악기를 위한 전문 복원 서비스를 제공합니다.',
    galleryTitle: '갤러리',
    galleryIntro: '공방의 악기 선택.',
    galleryEmpty: '새로운 작품을 준비 중입니다. 곧 다시 방문해 주세요.',
    contactTitle: '문의',
    contactIntro: '악기 주문이나 질문이 있으시면 연락 주세요.',
    contactName: '이름',
    contactEmail: '이메일',
    contactSubject: '제목',
    contactMessage: '메시지',
    contactSend: '메시지 보내기',
    contactSuccess: '메시지가 성공적으로 전송되었습니다. 가능한 빨리 답변 드리겠습니다.',
    contactError: '오류가 발생했습니다. 다시 시도해 주세요.',
    contactRateLimit: '요청이 너무 많습니다. 나중에 다시 시도해 주세요.',
    contactNameRequired: '이름을 입력해 주세요.',
    contactEmailRequired: '이메일 주소를 입력해 주세요.',
    contactEmailInvalid: '유효한 이메일 주소를 입력해 주세요.',
    contactMessageRequired: '메시지를 입력해 주세요.',
    contactInquiryType: '문의 유형',
    contactCountry: '국가 (선택)',
    inquiryTypeGeneral: '일반 질문',
    inquiryTypePurchase: '악기 구매',
    inquiryTypeRestoration: '복원 및 수리',
    inquiryTypeCustomBuild: '맞춤 제작',
    inquiryTypeSupplier: '공급업체 및 비즈니스',
    inquiryTypePress: '언론 및 협업',
    filterAll: '전체',
    filterViolin: '바이올린',
    filterViola: '비올라',
    filterCello: '첼로',
    filterBass: '더블베이스',
    view360: '360° 보기',
    view360Hint: '인터랙티브 360° 보기 가능',
  },
  zh: {
    brandName: 'Szoradi Instruments',
    tagline: '高级小提琴、中提琴、大提琴和低音提琴 — 德国柏林手工制作',
    subline: '为全球音乐家打造的手工弦乐器。',
    visitInstagram: '访问 Instagram',
    visitFacebook: '访问 Facebook',
    followText: '关注 Szoradi Instruments 的社交媒体，探索工艺、声音与创作。',
    copyright: '© 2025 Szoradi Instruments. 保留所有权利。',
    imageCaption: '仿卡罗·托诺尼大提琴',
    metaTitle: 'Szoradi Instruments | 高级小提琴、中提琴、大提琴和低音提琴 – 德国柏林手工制作',
    metaDescription: 'Szoradi Instruments 在德国柏林手工制作高级小提琴、中提琴、大提琴和低音提琴。',
    navHome: '首页',
    navAbout: '工坊',
    navInstruments: '乐器',
    navRestorations: '修复',
    navGallery: '画廊',
    navContact: '联系',
    aboutTitle: '关于工坊',
    aboutIntro: 'Szoradi Instruments 是一家位于柏林的工坊，致力于手工弦乐器制作的艺术。',
    aboutPhilosophy: '理念',
    aboutPhilosophyText: '每件乐器都是独一无二的作品，由传统塑造，经数十年经验精炼而成。',
    aboutCraftsmanship: '工艺',
    aboutCraftsmanshipText: '仅使用最优质的音木和经过时间考验的技术，每件乐器都经过从头到尾的精心制作。',
    instrumentsTitle: '乐器',
    instrumentsIntro: '每件乐器都完全手工制作，遵循古典乐器制作的伟大传统。',
    violinTitle: '小提琴',
    violinText: '以意大利大师的模型为基础，我们的小提琴提供温暖、投射性强的音色。',
    violaTitle: '中提琴',
    violaText: '丰富、深沉、洪亮 — 我们的中提琴为声音深度和演奏者的舒适度而打造。',
    celloTitle: '大提琴',
    celloText: '强劲而歌唱般的，我们的大提琴旨在以温暖和清晰填满音乐厅。',
    bassTitle: '低音提琴',
    bassText: '从管弦乐到独奏，我们的低音提琴提供深沉的共鸣和出色的响应性。',
    restorationsTitle: '修复',
    restorationsIntro: '通过专业修复保存名贵乐器的遗产。',
    restorationsText: '我们为古董和现代弦乐器提供专业修复服务。',
    galleryTitle: '画廊',
    galleryIntro: '工坊乐器精选。',
    galleryEmpty: '新作品正在准备中，请稍后再来。',
    contactTitle: '联系',
    contactIntro: '有兴趣定制乐器或有疑问？我们期待您的来信。',
    contactName: '姓名',
    contactEmail: '电子邮件',
    contactSubject: '主题',
    contactMessage: '留言',
    contactSend: '发送消息',
    contactSuccess: '您的消息已成功发送。我们会尽快回复。',
    contactError: '发生错误，请重试。',
    contactRateLimit: '请求过多，请稍后再试。',
    contactNameRequired: '请输入您的姓名。',
    contactEmailRequired: '请输入您的电子邮件地址。',
    contactEmailInvalid: '请输入有效的电子邮件地址。',
    contactMessageRequired: '请输入您的留言。',
    contactInquiryType: '咨询类型',
    contactCountry: '国家（可选）',
    inquiryTypeGeneral: '一般咨询',
    inquiryTypePurchase: '乐器购买',
    inquiryTypeRestoration: '修复与维修',
    inquiryTypeCustomBuild: '定制制作',
    inquiryTypeSupplier: '供应商与商务',
    inquiryTypePress: '媒体与合作',
    filterAll: '全部',
    filterViolin: '小提琴',
    filterViola: '中提琴',
    filterCello: '大提琴',
    filterBass: '低音提琴',
    view360: '360°视图',
    view360Hint: '可用互动360°视图',
  },
};

export const languageNames: Record<Language, string> = {
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
  fr: 'Français',
  es: 'Español',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
};

export const languageCodes: Record<Language, string> = {
  en: 'en',
  de: 'de',
  it: 'it',
  fr: 'fr',
  es: 'es',
  ja: 'ja',
  ko: 'ko',
  zh: 'zh',
};
